import React, { useEffect, useState } from 'react';
import { TrendingUp, Package, Users, Heart, DollarSign, Percent, MapPin, Mail, Eye } from 'lucide-react';
import './AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const currency = (v) => `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const MonthlyChart = ({ series, isTraffic = false }) => {
  const max = Math.max(1, ...series.map((s) => s.revenue));
  return (
    <div className="dash-chart">
      {series.map((s) => (
        <div className="dash-bar-wrap" key={s.month}>
          <div className="dash-bar-track">
            <div
              className="dash-bar-fill"
              style={{ height: `${Math.max(2, (s.revenue / max) * 100)}%` }}
              title={isTraffic ? `${s.revenue} views · ${s.orders} visitors` : `${currency(s.revenue)} · ${s.orders} orders`}
            />
          </div>
          <span className="dash-bar-label">{s.label}</span>
          <span className="dash-bar-value">{s.revenue > 0 ? (isTraffic ? s.revenue : currency(s.revenue)) : '—'}</span>
        </div>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/dashboard`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="dash-loading">Loading dashboard…</div>;
  if (!data) return <div className="dash-loading">Could not load dashboard.</div>;

  const { revenue, orders, artworks, top_sold, top_offered, top_countries, frame_preference, offers, newsletter, traffic } = data;

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      {/* Top KPI row */}
      <div className="dash-kpi-grid">
        <div className="dash-kpi">
          <span className="dash-kpi-icon"><DollarSign size={18} strokeWidth={1.5} /></span>
          <div>
            <span className="dash-kpi-label">Total revenue</span>
            <span className="dash-kpi-value">{currency(revenue.total)} <span className="dash-kpi-unit">USD</span></span>
          </div>
        </div>
        <div className="dash-kpi">
          <span className="dash-kpi-icon"><Eye size={18} strokeWidth={1.5} /></span>
          <div>
            <span className="dash-kpi-label">Visits (7 days)</span>
            <span className="dash-kpi-value">{traffic?.views_week ?? 0}<span className="dash-kpi-unit"> · {traffic?.visitors_week ?? 0} people</span></span>
          </div>
        </div>
        <div className="dash-kpi">
          <span className="dash-kpi-icon"><Package size={18} strokeWidth={1.5} /></span>
          <div>
            <span className="dash-kpi-label">Orders paid</span>
            <span className="dash-kpi-value">{orders.paid}<span className="dash-kpi-unit"> / {orders.total}</span></span>
          </div>
        </div>
        <div className="dash-kpi">
          <span className="dash-kpi-icon"><Users size={18} strokeWidth={1.5} /></span>
          <div>
            <span className="dash-kpi-label">Subscribers</span>
            <span className="dash-kpi-value">{newsletter.subscribers}</span>
          </div>
        </div>
      </div>

      {/* Traffic 14-day chart */}
      <div className="dash-card">
        <div className="dash-card-header">
          <span className="dash-eyebrow">— Traffic</span>
          <h3>Visits · last 14 days</h3>
          <div className="dash-mini-kpis">
            <span><strong>{traffic?.views_today ?? 0}</strong> today</span>
            <span><strong>{traffic?.views_month ?? 0}</strong> this month</span>
            <span><strong>{traffic?.mobile ?? 0}</strong> mobile · <strong>{traffic?.desktop ?? 0}</strong> desktop</span>
          </div>
        </div>
        <MonthlyChart series={(traffic?.daily_series || []).map(d => ({ month: d.date, label: d.label, revenue: d.views, orders: d.visitors }))} isTraffic />
      </div>

      {/* Revenue chart */}
      <div className="dash-card">
        <div className="dash-card-header">
          <span className="dash-eyebrow">— Revenue</span>
          <h3>Last 6 months</h3>
        </div>
        <MonthlyChart series={revenue.monthly_series} />
      </div>

      {/* Traffic sources + top viewed artworks */}
      <div className="dash-row-3">
        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— Where visits come from</span>
          {!traffic?.top_sources || traffic.top_sources.length === 0 ? (
            <p className="dash-empty">No data yet.</p>
          ) : (
            <ol className="dash-list">
              {traffic.top_sources.map((s) => (
                <li key={s.source}>
                  <span style={{ textTransform: 'capitalize' }}>{s.source}</span>
                  <span className="dash-num">{s.count}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— <Eye size={12} strokeWidth={2} style={{ verticalAlign: 'middle' }} /> Most viewed works</span>
          {!traffic?.top_viewed_artworks || traffic.top_viewed_artworks.length === 0 ? (
            <p className="dash-empty">No data yet.</p>
          ) : (
            <ol className="dash-list">
              {traffic.top_viewed_artworks.map((t) => (
                <li key={t.title}>
                  <span translate="no">{t.title}</span>
                  <span className="dash-num">{t.count}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— Average order</span>
          <div className="dash-inv">
            <div className="dash-inv-row dash-inv-value">
              <span>Avg order value</span>
              <span className="dash-num" translate="no">{currency(revenue.average_order)} USD</span>
            </div>
            <div className="dash-inv-row">
              <span>PayPal orders</span>
              <span className="dash-num">{orders.by_payment_method?.paypal || 0}</span>
            </div>
            <div className="dash-inv-row">
              <span>Transfer orders</span>
              <span className="dash-num">{orders.by_payment_method?.transfer || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Middle row: Inventory + Offers + Newsletter */}
      <div className="dash-row-3">
        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— Inventory</span>
          <div className="dash-inv">
            <div className="dash-inv-row">
              <span>Available</span>
              <span className="dash-num">{artworks.available}</span>
            </div>
            <div className="dash-inv-row">
              <span>Sold</span>
              <span className="dash-num">{artworks.sold}</span>
            </div>
            <div className="dash-inv-row">
              <span>Works · Sculptures</span>
              <span className="dash-num">{artworks.works} · {artworks.sculptures}</span>
            </div>
            <div className="dash-inv-row dash-inv-value">
              <span>Available catalog value</span>
              <span className="dash-num" translate="no">{currency(artworks.inventory_value_available)} USD</span>
            </div>
          </div>
        </div>

        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— Offers</span>
          <div className="dash-inv">
            <div className="dash-inv-row">
              <span>Total received</span>
              <span className="dash-num">{offers.total}</span>
            </div>
            <div className="dash-inv-row">
              <span>Pending</span>
              <span className="dash-num">{offers.pending}</span>
            </div>
            <div className="dash-inv-row">
              <span>Accepted</span>
              <span className="dash-num">{offers.accepted}</span>
            </div>
          </div>
        </div>

        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— Newsletter</span>
          <div className="dash-inv">
            <div className="dash-inv-row">
              <span><Mail size={14} strokeWidth={1.5} style={{ marginRight: 6, verticalAlign: 'middle' }} />Subscribers</span>
              <span className="dash-num">{newsletter.subscribers}</span>
            </div>
            <div className="dash-inv-row">
              <span><Percent size={14} strokeWidth={1.5} style={{ marginRight: 6, verticalAlign: 'middle' }} />Active discount codes</span>
              <span className="dash-num">{newsletter.active_codes}</span>
            </div>
            <div className="dash-inv-row">
              <span>Pending orders</span>
              <span className="dash-num">{orders.pending}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom row: Top artworks + countries + frames */}
      <div className="dash-row-3">
        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— Top sold</span>
          {top_sold.length === 0 ? (
            <p className="dash-empty">No sales yet.</p>
          ) : (
            <ol className="dash-list">
              {top_sold.map((t) => (
                <li key={t.title}>
                  <span translate="no">{t.title}</span>
                  <span className="dash-num">{t.count}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— <Heart size={12} strokeWidth={2} style={{ verticalAlign: 'middle' }} /> Most requested</span>
          {top_offered.length === 0 ? (
            <p className="dash-empty">No offers yet.</p>
          ) : (
            <ol className="dash-list">
              {top_offered.map((t) => (
                <li key={t.title}>
                  <span translate="no">{t.title}</span>
                  <span className="dash-num">{t.count}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="dash-card dash-card-sm">
          <span className="dash-eyebrow">— <MapPin size={12} strokeWidth={2} style={{ verticalAlign: 'middle' }} /> Top countries</span>
          {top_countries.length === 0 ? (
            <p className="dash-empty">No orders yet.</p>
          ) : (
            <ol className="dash-list">
              {top_countries.map((c) => (
                <li key={c.country}>
                  <span translate="no">{c.country}</span>
                  <span className="dash-num">{c.count}</span>
                </li>
              ))}
            </ol>
          )}
          {Object.keys(frame_preference || {}).length > 0 && (
            <>
              <span className="dash-eyebrow" style={{ marginTop: '1rem' }}>— Frame preference</span>
              <div className="dash-frame-row">
                {Object.entries(frame_preference).map(([f, n]) => (
                  <span key={f} className="dash-frame-tag">{f}: {n}</span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
